import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AdminApp from "@/components/admin/AdminApp";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Copy,
  ExternalLink,
  Hash,
  Home,
  KeyRound,
  LockKeyhole,
  Mail,
  Megaphone,
  MessageCircle,
  Play,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Timer,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "imapflix_session_token";

const KIND_OPTIONS = [
  { id: "signin", label: "Sign-in Code", icon: KeyRound, premium: true },
  { id: "reset", label: "Reset", icon: RotateCcw, premium: true },
  { id: "sixdigit", label: "6-Digit Code", icon: Hash, premium: true },
  { id: "household", label: "Household Mail", icon: Home, premium: false },
];

const KIND_LOOKUP = Object.fromEntries(KIND_OPTIONS.map((option) => [option.id, option]));
const formatSender = (value = "") => value.split("<")[0].trim() || value;
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const readSessionToken = () => {
  try {
    return localStorage.getItem(SESSION_KEY) || "";
  } catch {
    return "";
  }
};
const writeSessionToken = (value) => {
  try {
    if (value) localStorage.setItem(SESSION_KEY, value);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* noop */
  }
};

const formatExpiryLabel = (isoString) => {
  if (!isoString) return "";
  const target = new Date(isoString);
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return "Expired";
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

// --------------------------------------------------------------------------- //
// Components
// --------------------------------------------------------------------------- //

function KindChip({ option, active, onSelect, disabled, premiumActive }) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      className={active ? "kind-chip active" : "kind-chip"}
      onClick={() => onSelect(option.id)}
      disabled={disabled}
      data-testid={`kind-chip-${option.id}`}
    >
      <Icon size={15} />
      <span>{option.label}</span>
      {option.premium ? (
        <span className={premiumActive ? "kind-badge unlocked" : "kind-badge locked"}>
          {premiumActive ? "PREMIUM" : "KEY"}
        </span>
      ) : (
        <span className="kind-badge free">FREE</span>
      )}
    </button>
  );
}

function RedeemModal({ open, onClose, onSuccess }) {
  const [keyInput, setKeyInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) {
      setKeyInput("");
      setErrorMsg("");
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    const value = keyInput.trim().toUpperCase();
    if (value.length < 6) {
      setErrorMsg("Please paste the full access key.");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const response = await axios.post(`${API}/keys/redeem`, { key: value });
      onSuccess(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMsg(typeof detail === "string" ? detail : "Could not redeem this key.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <motion.div
      className="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="redeem-modal"
    >
      <motion.form
        className="modal-panel"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24 }}
        onSubmit={submit}
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close redeem modal"
          data-testid="close-redeem-modal"
        >
          <X size={18} />
        </button>
        <div className="modal-icon">
          <KeyRound size={22} />
        </div>
        <h3>Redeem access key</h3>
        <p className="modal-copy">
          Premium lookups (Sign-in Code, Reset, 6-Digit Code) need an access key. Paste yours below —
          Household Mail stays free.
        </p>
        <label className="modal-input">
          <KeyRound size={16} />
          <input
            type="text"
            autoComplete="off"
            inputMode="text"
            placeholder="ANURAGH-XXXX-XXXX-XXXX"
            value={keyInput}
            onChange={(event) => setKeyInput(event.target.value)}
            disabled={submitting}
            data-testid="redeem-key-input"
          />
        </label>
        {errorMsg ? (
          <div className="inline-error" role="alert" data-testid="redeem-error">
            {errorMsg}
          </div>
        ) : null}
        <button
          type="submit"
          className="primary-button full-button"
          disabled={submitting}
          data-testid="redeem-submit"
        >
          {submitting ? <RefreshCw size={16} className="spin" /> : <ShieldCheck size={16} />}
          {submitting ? "Redeeming…" : "Redeem key"}
        </button>
        <button
          type="button"
          className="text-button"
          onClick={onClose}
          data-testid="redeem-cancel"
        >
          Not now
        </button>
      </motion.form>
    </motion.div>
  );
}

function ResultCard({ result, kind, refreshing, onRefresh, onReset }) {
  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (!result.found) {
    return (
      <motion.section
        className="result-card empty"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        data-testid="result-empty"
      >
        <div className="result-eyebrow"><Search size={13} /> NO MATCH</div>
        <h2>No matching email found for <em>{result.address}</em></h2>
        <p>
          We couldn't find a recent <strong>{KIND_LOOKUP[kind]?.label}</strong> message addressed to that
          address yet. Wait a few seconds, then hit Refresh.
        </p>
        <div className="result-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-button"
          >
            <RefreshCw size={14} className={refreshing ? "spin" : ""} />
            {refreshing ? "Checking…" : "Refresh Latest Mail"}
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={onReset}
            data-testid="reset-button-empty"
          >
            <Play size={14} fill="currentColor" /> Fetch Another
          </button>
        </div>
      </motion.section>
    );
  }

  const showCode = Boolean(result.code) && (kind === "signin" || kind === "sixdigit" || kind === "household");
  const showLinks = (kind === "reset" || kind === "household") && Array.isArray(result.links) && result.links.length > 0;

  return (
    <motion.section
      className="result-card"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      data-testid="result-card"
    >
      <header className="result-header">
        <div className="result-eyebrow">
          <span className="red-dot" /> LATEST {KIND_LOOKUP[kind]?.label.toUpperCase()}
        </div>
        <h2 data-testid="result-subject">{result.subject}</h2>
        <p className="result-meta" data-testid="result-meta">
          <strong>{formatSender(result.sender)}</strong> · to <em>{result.address}</em> · {result.date}
        </p>
      </header>

      {showCode ? (
        <div className="codes-block" data-testid="codes-block">
          <span className="block-label"><Sparkles size={12} /> Code</span>
          <div className="codes-row">
            <button
              type="button"
              className="code-pill"
              onClick={() => copyValue(result.code)}
              title="Click to copy"
              data-testid="result-code"
            >
              <span>{result.code}</span>
              <Copy size={13} />
            </button>
          </div>
        </div>
      ) : null}

      {showLinks ? (
        <div className="links-block" data-testid="links-block">
          <span className="block-label"><ExternalLink size={12} /> Action link</span>
          <ul>
            {result.links.map((link) => (
              <li key={link.url}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`action-link-${link.url}`}
                >
                  <span>{link.label}</span>
                  <ExternalLink size={13} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="preview-block" data-testid="preview-block">
        <span className="block-label">Message preview</span>
        <p>{result.preview}</p>
      </div>

      <footer className="result-footer">
        <span className="read-only-note"><LockKeyhole size={13} /> Only this message was retrieved</span>
        <div className="result-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-button"
          >
            <RefreshCw size={14} className={refreshing ? "spin" : ""} />
            {refreshing ? "Checking…" : "Refresh Latest Mail"}
          </button>
          <button
            type="button"
            className="secondary-button ghost"
            onClick={onReset}
            data-testid="reset-button"
          >
            <Play size={14} fill="currentColor" /> Fetch Another
          </button>
        </div>
      </footer>
    </motion.section>
  );
}

// --------------------------------------------------------------------------- //
// App
// --------------------------------------------------------------------------- //

function PublicLookup() {
  const [kind, setKind] = useState("household");
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);
  const [lastLookup, setLastLookup] = useState(null); // { email, kind }
  const [error, setError] = useState("");

  const [sessionToken, setSessionToken] = useState(readSessionToken);
  const [sessionInfo, setSessionInfo] = useState({ active: false, expires_at: null });
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [supportUsername, setSupportUsername] = useState("");
  const [advert, setAdvert] = useState(null);
  const pendingKindRef = useRef(null);

  const activeKindLabel = useMemo(() => KIND_LOOKUP[kind]?.label ?? "Sign-in Code", [kind]);
  const premiumActive = sessionInfo.active;
  const supportUrl = supportUsername ? `https://t.me/${supportUsername}` : "";
  const advertUrl = advert?.telegram_username ? `https://t.me/${advert.telegram_username}` : "";

  // Refresh session status on load / when token changes
  const refreshSessionStatus = useCallback(async (token) => {
    if (!token) {
      setSessionInfo({ active: false, expires_at: null });
      return { active: false };
    }
    try {
      const response = await axios.get(`${API}/session`, { headers: { "X-Session-Token": token } });
      const data = response.data || {};
      setSessionInfo(data);
      if (!data.active) writeSessionToken("");
      return data;
    } catch (err) {
      // Temporary network/API failure: keep the existing session state.
      console.warn("Session check temporarily failed; keeping existing session.", err);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshSessionStatus(sessionToken);
  }, [sessionToken, refreshSessionStatus]);

  // Fetch the public support username once on load.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/config`)
      .then((response) => {
        if (!cancelled) setSupportUsername((response.data?.support_username || "").trim());
      })
      .catch(() => { /* support link is optional */ });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the current advertisement on load, then poll every 60 seconds.
  useEffect(() => {
    let cancelled = false;
    const loadAd = async () => {
      try {
        const response = await axios.get(`${API}/ad`);
        if (cancelled) return;
        setAdvert(response.data?.active ? response.data : null);
      } catch {
        if (!cancelled) setAdvert(null);
      }
    };
    loadAd();
    const timer = setInterval(loadAd, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const authHeaders = useCallback(() => {
    return sessionToken ? { "X-Session-Token": sessionToken } : {};
  }, [sessionToken]);

  const handleKindChange = (nextId) => {
    if (nextId === kind) return;
    const option = KIND_LOOKUP[nextId];
    if (option?.premium && !premiumActive) {
      pendingKindRef.current = nextId;
      setRedeemOpen(true);
      return;
    }
    setKind(nextId);
    setResult(null);
    setLastLookup(null);
    setError("");
  };

  const requiresPremium = (kindId) => KIND_LOOKUP[kindId]?.premium && !premiumActive;

  const doLookup = async ({ email, kind: lookupKind, isRefresh }) => {
    if (requiresPremium(lookupKind)) {
      pendingKindRef.current = lookupKind;
      setRedeemOpen(true);
      return { blocked: true };
    }
    try {
      const response = await axios.post(
        `${API}/mail/latest`,
        { email, kind: lookupKind, refresh: Boolean(isRefresh) },
        { headers: authHeaders() },
      );
      return { data: response.data };
    } catch (err) {
      const statusCode = err.response?.status;
      const detail = err.response?.data?.detail;
      if (statusCode === 401) {
        writeSessionToken("");
        setSessionToken("");
        setSessionInfo({ active: false, expires_at: null });
        pendingKindRef.current = lookupKind;
        setRedeemOpen(true);
        toast.error("Access expired. Redeem a new key.");
        return { blocked: true };
      }
      const message = typeof detail === "string" ? detail : (isRefresh ? "Refresh failed." : "Lookup failed.");
      return { error: message };
    }
  };

  const submitLookup = async (event) => {
    event?.preventDefault?.();
    const value = emailInput.trim().toLowerCase();
    if (!isValidEmail(value)) {
      setError("Please enter a valid email address.");
      toast.error("Enter a valid email address");
      return;
    }
    setError("");
    setLoading(true);
    const outcome = await doLookup({ email: value, kind, isRefresh: false });
    setLoading(false);
    if (outcome.blocked) return;
    if (outcome.error) {
      setError(outcome.error);
      toast.error(outcome.error);
      return;
    }
    setResult(outcome.data);
    setLastLookup({ email: value, kind });
    if (outcome.data.found) toast.success(`${activeKindLabel} loaded`);
    else toast("No matching email found", { icon: "🔍" });
  };

  const refresh = async () => {
    if (!lastLookup) return;
    setRefreshing(true);
    setError("");
    const outcome = await doLookup({ email: lastLookup.email, kind: lastLookup.kind, isRefresh: true });
    setRefreshing(false);
    if (outcome.blocked) return;
    if (outcome.error) {
      toast.error(outcome.error);
      return;
    }
    const next = outcome.data;
    if (!next.found) {
      if (result?.found) {
        toast("No new mail yet", { icon: "⏱" });
        return;
      }
      setResult(next);
      return;
    }
    if (result?.found && result.uid && next.uid && result.uid === next.uid) {
      toast("No new mail yet", { icon: "⏱" });
      return;
    }
    setResult(next);
    toast.success("New mail loaded");
  };

  const reset = () => {
    setResult(null);
    setError("");
    setLastLookup(null);
    setEmailInput("");
  };

  const handleRedeemSuccess = async (data) => {
    if (data?.session_token) {
      writeSessionToken(data.session_token);
      setSessionToken(data.session_token);
      setSessionInfo({
        active: true,
        expires_at: data.expires_at,
        duration_seconds: data.duration_seconds,
      });
      setRedeemOpen(false);
      toast.success("Premium unlocked");
      const pending = pendingKindRef.current;
      pendingKindRef.current = null;
      if (pending) {
        setKind(pending);
        setResult(null);
        setLastLookup(null);
      }
    }
  };

  const handleSignOut = () => {
    writeSessionToken("");
    setSessionToken("");
    setSessionInfo({ active: false, expires_at: null });
    if (KIND_LOOKUP[kind]?.premium) {
      setKind("household");
      setResult(null);
      setLastLookup(null);
    }
    toast("Signed out of premium access");
  };

  return (
    <div className="app-shell">
      <Toaster position="top-right" theme="dark" richColors />

      <header className="site-header">
        <button
          type="button"
          className="brand-lockup"
          onClick={reset}
          data-testid="brand-home-button"
        >
          <span className="brand-mark">Anur<span>agh</span></span>
          <span className="brand-caption">NETFLIX MAIL LOOKUP</span>
        </button>
        <div className="header-actions">
          <a href="/admin" className="support-chip" data-testid="admin-link">
            <ShieldCheck size={14} />
            <span>Admin</span>
          </a>
          {supportUrl ? (
            <a
              href={supportUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="support-chip"
              data-testid="support-link"
            >
              <MessageCircle size={14} />
              <span>Telegram Support</span>
            </a>
          ) : null}
          {premiumActive ? (
            <div className="session-chip active" data-testid="session-chip">
              <Timer size={14} />
              <span>{formatExpiryLabel(sessionInfo.expires_at)}</span>
              <button
                type="button"
                className="session-signout"
                onClick={handleSignOut}
                data-testid="session-signout"
                aria-label="Sign out of premium"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="session-chip"
              onClick={() => setRedeemOpen(true)}
              data-testid="header-redeem-button"
            >
              <KeyRound size={14} />
              <span>Redeem key</span>
            </button>
          )}
        </div>
      </header>

      <main className="stage">
        <div className="stage-art" aria-hidden />
        <div className="stage-scrim" aria-hidden />

        <section className="stage-content">
          {advert && advertUrl ? (
            <motion.aside
              className="ad-banner"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="ad-banner"
            >
              <div className="ad-left">
                <span className="ad-badge">
                  <Megaphone size={12} /> ANNOUNCEMENT
                </span>
                <p className="ad-text" data-testid="ad-text">{advert.text}</p>
              </div>
              <div className="ad-right">
                <div className="ad-contact">
                  <span className="ad-contact-label">TELEGRAM</span>
                  <a
                    href={advertUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="ad-username"
                    data-testid="ad-username-link"
                  >
                    @{advert.telegram_username}
                  </a>
                </div>
                <a
                  href={advertUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="ad-cta"
                  data-testid="ad-cta"
                >
                  <Send size={14} /> Message Now →
                </a>
              </div>
            </motion.aside>
          ) : null}

          <div className="eyebrow" data-testid="hero-eyebrow">
            <span className="red-dot" /> PRIVATE MAILBOX LOOKUP
          </div>
          <h1 data-testid="hero-heading">
            Get your <em>{activeKindLabel}</em>.
          </h1>

          <div className="kind-row" role="tablist" aria-label="Lookup category" data-testid="kind-row">
            {KIND_OPTIONS.map((option) => (
              <KindChip
                key={option.id}
                option={option}
                active={option.id === kind}
                onSelect={handleKindChange}
                disabled={loading || refreshing}
                premiumActive={premiumActive}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                onSubmit={submitLookup}
                className="lookup-form"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                data-testid="lookup-form"
              >
                <label className="lookup-input">
                  <Mail size={17} />
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    disabled={loading}
                    data-testid="email-input"
                  />
                </label>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={loading}
                  data-testid="fetch-button"
                >
                  {loading ? <RefreshCw size={16} className="spin" /> : <Play size={16} fill="currentColor" />}
                  {loading ? "Fetching…" : "Fetch latest"}
                </button>
              </motion.form>
            ) : null}
          </AnimatePresence>

          {error ? (
            <div className="inline-error" role="alert" data-testid="inline-error">
              {error}
            </div>
          ) : null}

          <AnimatePresence>
            {result ? (
              <ResultCard
                key="result"
                result={result}
                kind={kind}
                refreshing={refreshing}
                onRefresh={refresh}
                onReset={reset}
              />
            ) : null}
          </AnimatePresence>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-brand">
          <span className="brand-mark">Anur<span>agh</span></span>
          <span>Server-side Gmail IMAP · zero credentials in the browser</span>
        </div>
        <div className="footer-actions">
          {supportUrl ? (
            <a
              href={supportUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="footer-support"
              data-testid="footer-support-link"
            >
              <MessageCircle size={13} /> Telegram Support
            </a>
          ) : null}
          <span data-testid="read-only-footer">
            <LockKeyhole size={13} /> Read-only by design
          </span>
        </div>
      </footer>

      <AnimatePresence>
        {redeemOpen ? (
          <RedeemModal
            key="redeem"
            open={redeemOpen}
            onClose={() => {
              pendingKindRef.current = null;
              setRedeemOpen(false);
            }}
            onSuccess={handleRedeemSuccess}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicLookup />} />
        <Route path="/admin/*" element={<AdminApp />} />
      </Routes>
    </BrowserRouter>
  );
}
